// @ts-nocheck
import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {MailOrderCustomHeader} from './MailOrderCustomHeader'
import {MailOrderActionsCell} from './MailOrderActionsCell'

const STATUS_BADGE_CLASS = {
  pending: 'badge-light-warning',
  partially_paid: 'badge-light-info',
  paid: 'badge-light-success',
  expired: 'badge-light-danger',
  cancelled: 'badge-light-dark',
}

const STATUS_LABEL_KEY = {
  pending: 'MAIL_ORDER.STATUS.PENDING',
  partially_paid: 'MAIL_ORDER.STATUS.PARTIALLY_PAID',
  paid: 'MAIL_ORDER.STATUS.PAID',
  expired: 'MAIL_ORDER.STATUS.EXPIRED',
  cancelled: 'MAIL_ORDER.STATUS.CANCELLED',
}

const formatAmount = (amount: number, currency: string) => {
  if (typeof amount !== 'number') return '-'
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'TND',
    }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`
  }
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('fr-FR')
}

export const useMailOrdersColumns = () => {
  const intl = useIntl()
  const navigate = useNavigate()

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.TOUR'})}
            className='min-w-150px'
          />
        ),
        id: 'circuitName',
        Cell: ({...props}) => {
          const mailOrder = props.data[props.row.index]
          return (
            <div className='d-flex flex-column'>
              <span
                className='text-gray-800 text-hover-primary mb-1 cursor-pointer'
                onClick={() => navigate(`/apps/mail-orders/${mailOrder._id}`)}
              >
                {mailOrder.circuitName}
              </span>
              <span className='text-muted fs-7'>
                {formatDate(mailOrder.circuitStartDate)} → {formatDate(mailOrder.circuitEndDate)}
              </span>
            </div>
          )
        },
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.CLIENT'})}
            className='min-w-150px'
          />
        ),
        id: 'clientName',
        Cell: ({...props}) => {
          const mailOrder = props.data[props.row.index]
          return (
            <div className='d-flex flex-column'>
              <span className='text-gray-800 mb-1'>{mailOrder.clientName}</span>
              <span className='text-muted fs-7'>{mailOrder.clientEmail}</span>
            </div>
          )
        },
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.TOTAL_PRICE'})}
            className='min-w-100px'
          />
        ),
        id: 'totalPrice',
        Cell: ({...props}) => {
          const mailOrder = props.data[props.row.index]
          return <span>{formatAmount(mailOrder.totalPrice, mailOrder.currency)}</span>
        },
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PAID_AMOUNT'})}
            className='min-w-125px'
          />
        ),
        id: 'paidAmount',
        Cell: ({...props}) => {
          const mailOrder = props.data[props.row.index]
          return (
            <div className='d-flex flex-column'>
              <span>{formatAmount(mailOrder.paidAmount, mailOrder.currency)}</span>
              <span className='text-muted fs-7'>
                {intl.formatMessage(
                  {id: 'MAIL_ORDER.INSTALLMENTS_COUNT'},
                  {count: mailOrder.installmentsCount || 0}
                )}
              </span>
            </div>
          )
        },
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.STATUS'})}
            className='min-w-100px'
          />
        ),
        id: 'status',
        Cell: ({...props}) => {
          const status = props.data[props.row.index].status
          return (
            <span className={`badge ${STATUS_BADGE_CLASS[status] || 'badge-light-secondary'}`}>
              {intl.formatMessage({id: STATUS_LABEL_KEY[status] || 'MAIL_ORDER.STATUS.PENDING'})}
            </span>
          )
        },
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader
            tableProps={props}
            title={intl.formatMessage({id: 'COLUMNS.CREATED_AT'})}
            className='min-w-100px'
          />
        ),
        id: 'createdAt',
        Cell: ({...props}) => <span>{formatDate(props.data[props.row.index].createdAt)}</span>,
      },
      {
        Header: (props) => (
          <MailOrderCustomHeader tableProps={props} title='' className='text-end min-w-100px' />
        ),
        id: 'actions',
        Cell: ({...props}) => {
          const mailOrder = props.data[props.row.index]
          return <MailOrderActionsCell id={mailOrder._id} />
        },
      },
    ],
    [intl, navigate]
  )

  return columns
}
